import { Injectable } from '@angular/core';

import { Iban } from './types/iban';
import { Bank } from './types/bank';
import { CountryBankData } from './types/bank-data/country-bank-data';
import { CountryBankDataFactory } from './types/bank-data/country-bank-data.factory';
import { banks } from './data/banks';
import { IbanService } from './iban.service';
import { SortCode } from './types/sort-code';

export enum FindResult {
  Success,
  /**
   * There is no data available for this country
   */
  NoCountryData,
  BankNotFound,
  Undefined,
}

interface FindBankResult {
  result: FindResult;
  countryBankData?: CountryBankData;
  bank?: Bank;
}

@Injectable({
  providedIn: 'root',
})
export class BankService {
  private _countryBankData: Map<string, CountryBankData> = new Map();

  constructor(private ibanService: IbanService) {
    this._countryBankData = new Map(
      Object.entries(banks).map(([key, data]) => [
        key,
        CountryBankDataFactory(key, data),
      ]),
    );
  }

  /**
   * @param iban The iban for which a bank should be found
   *
   * @returns
   * An object indicating the result of the find operation.
   */
  public findByIban(iban: Iban): FindBankResult {
    const { countryCode } = iban;
    const result: FindBankResult = {
      result: FindResult.Undefined,
    };

    // First, look for the country bank
    const countryBankData = this._countryBankData.get(countryCode);
    if (!countryBankData) {
      return {
        ...result,
        result: FindResult.NoCountryData,
      };
    }

    // Then look for the bank
    const bankInfo = this.ibanService.getBankInfo(iban);
    result.countryBankData = countryBankData;
    const bank = countryBankData.findBank(bankInfo.bankId, bankInfo.branchId);
    if (!bank) {
      return {
        ...result,
        result: FindResult.BankNotFound,
      };
    }

    // Finally, return everything we found
    result.bank = bank;
    return {
      ...result,
      result: FindResult.Success,
    };
  }

  /**
   * @param sortCode
   * @returns The Bank of the given Sort Code
   */
  public findBySortCode(sortCode: SortCode): FindBankResult {
    const result: FindBankResult = {
      result: FindResult.Undefined,
    };

    const countryBankData = this._countryBankData.get('GB');

    // look for the bank
    const bank = countryBankData.findBank(undefined, sortCode.format(''));

    // no bank found
    if (!bank) {
      return {
        ...result,
        result: FindResult.BankNotFound,
      };
    }

    // bank found
    result.bank = bank;
    return {
      ...result,
      result: FindResult.Success,
    };
  }
}
